// Admin-only kit manager (drum + bass). Upload audio samples per slot,
// preview, delete, rename labels (double-click), and pick which kit is the
// default. The default kit is loaded by drumKitManager / bassKitManager and
// intercepts triggers in both the Training Lab playback (GenPlayer) and the
// DAW (AudioEngine).
import React, { useEffect, useRef, useState } from "react";
import API from "../api";
import drumKitManager from "../audio/drumKitManager";
import bassKitManager from "../audio/bassKitManager";
import melodyKitManager from "../audio/melodyKitManager";
import { toast } from "sonner";
import { UploadSimple, Trash, Play, Pause, Plus, MusicNotes, CheckCircle, Star, SpinnerGap } from "@phosphor-icons/react";
import { notifyKitsChanged } from "../utils/kitsBus";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

function SlotDropZone({ kit, slot, onChange, apiBase }) {
  const inputRef = useRef(null);
  const audioRef = useRef(null);
  const labelInputRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [playing, setPlaying] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const [editingLabel, setEditingLabel] = useState(false);
  const sample = kit?.samples?.[slot];
  const customLabel = kit?.labels?.[slot];
  const displayLabel = (customLabel && customLabel.trim()) || slot;
  const [labelDraft, setLabelDraft] = useState(displayLabel);

  useEffect(() => {
    // v19.7 (iter48) — Preserve in-progress edits. Without this guard,
    // an unrelated kits-changed pulse (e.g. another admin uploading
    // to a different kit in another tab) re-renders this slot with
    // the same displayLabel, retriggering the effect and blowing
    // away the producer's half-typed rename. Only re-sync the draft
    // when the field is NOT actively being edited.
    if (!editingLabel) setLabelDraft(displayLabel);
  }, [displayLabel, editingLabel]);
  useEffect(() => {
    if (editingLabel && labelInputRef.current) {
      labelInputRef.current.focus();
      labelInputRef.current.select();
    }
  }, [editingLabel]);

  const saveLabel = async () => {
    const next = labelDraft.trim();
    if (next === displayLabel) { setEditingLabel(false); return; }
    setEditingLabel(false);
    try {
      const { data } = await API.put(`${apiBase}/${kit.id}/labels/${slot}`, { label: next });
      onChange(data.kit);
      notifyKitsChanged({ source: apiBase, op: "rename-label", kitId: kit.id, slot });
      toast.success(next ? `${slot} → ${next}` : `${slot} label reset`);
    } catch (e) {
      toast.error("Rename failed: " + (e?.response?.data?.detail || e.message));
      setLabelDraft(displayLabel);
    }
  };

  const upload = async (file) => {
    if (!file) return;
    if (!file.type.startsWith("audio/") && !/\.(wav|mp3|ogg|flac|m4a|aac)$/i.test(file.name)) {
      toast.error("Only audio files are accepted"); return;
    }
    if (file.size > 2 * 1024 * 1024) {
      toast.error("Sample too large — 2MB max per slot"); return;
    }
    setBusy(true);
    try {
      const form = new FormData();
      form.append("file", file);
      const { data } = await API.post(`${apiBase}/${kit.id}/sample/${slot}`, form, { headers: { "Content-Type": "multipart/form-data" } });
      onChange(data.kit);
      notifyKitsChanged({ source: apiBase, op: "upload-sample", kitId: kit.id, slot });
      toast.success(`${slot} loaded`);
    } catch (e) {
      toast.error(`Upload failed: ${e?.response?.data?.detail || e.message}`);
    } finally { setBusy(false); }
  };

  const remove = async (e) => {
    e.stopPropagation();
    if (!sample) return;
    setBusy(true);
    try {
      const { data } = await API.delete(`${apiBase}/${kit.id}/sample/${slot}`);
      onChange(data.kit);
      notifyKitsChanged({ source: apiBase, op: "delete-sample", kitId: kit.id, slot });
    } catch (e2) { toast.error("Delete failed: " + (e2?.response?.data?.detail || e2.message)); }
    finally { setBusy(false); }
  };

  const togglePreview = (e) => {
    e.stopPropagation();
    if (!sample) return;
    const a = audioRef.current;
    if (!a) return;
    if (playing) { a.pause(); a.currentTime = 0; setPlaying(false); }
    else { a.currentTime = 0; a.play(); setPlaying(true); }
  };

  return (
    <div
      onClick={() => !busy && inputRef.current?.click()}
      onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
      onDragLeave={() => setDragOver(false)}
      onDrop={(e) => {
        e.preventDefault(); setDragOver(false);
        const f = e.dataTransfer.files?.[0];
        if (f) upload(f);
      }}
      className="rounded-lg p-3 cursor-pointer transition relative flex flex-col gap-1.5"
      style={{
        background: sample ? "#0a1a12" : "#0a0a0a",
        border: `1.5px ${dragOver ? "solid #34d399" : sample ? "solid #10b98155" : "dashed #262626"}`,
        minHeight: 92,
      }}
      data-testid={`kit-slot-${slot}`}
    >
      <div className="flex items-center justify-between">
        {editingLabel ? (
          <input
            ref={labelInputRef}
            value={labelDraft}
            onChange={(e) => setLabelDraft(e.target.value)}
            onClick={(e) => e.stopPropagation()}
            onKeyDown={(e) => {
              e.stopPropagation();
              if (e.key === "Enter") saveLabel();
              else if (e.key === "Escape") { setLabelDraft(displayLabel); setEditingLabel(false); }
            }}
            onBlur={saveLabel}
            maxLength={32}
            placeholder={slot}
            className="bg-transparent text-[11px] font-mono uppercase tracking-wider font-bold outline-none px-1 -ml-1 rounded w-24"
            style={{ color: "#34d399", border: "1px solid #10b981aa" }}
            data-testid={`kit-slot-${slot}-label-input`}
          />
        ) : (
          <div
            onClick={(e) => e.stopPropagation()}
            onDoubleClick={(e) => { e.stopPropagation(); setEditingLabel(true); }}
            className="text-[11px] font-mono uppercase tracking-wider font-bold cursor-text select-none"
            style={{ color: sample ? "#34d399" : "#71717a" }}
            title="Double-click to rename"
            data-testid={`kit-slot-${slot}-label`}
          >
            {displayLabel}
          </div>
        )}
        {sample && (
          <div className="flex items-center gap-1">
            <button onClick={togglePreview} className="p-1 rounded hover:bg-white/10" title="Preview" data-testid={`kit-slot-${slot}-preview`}>
              {playing ? <Pause size={12} weight="fill" /> : <Play size={12} weight="fill" />}
            </button>
            <button onClick={remove} className="p-1 rounded hover:bg-red-500/20 text-red-400" title="Remove" data-testid={`kit-slot-${slot}-remove`}>
              <Trash size={12} weight="bold" />
            </button>
          </div>
        )}
      </div>
      {busy ? (
        <div className="flex items-center gap-2 text-[11px] text-white/60"><SpinnerGap size={12} weight="bold" className="animate-spin" /> uploading…</div>
      ) : sample ? (
        <div className="text-[10px] text-white/50 font-mono truncate" title={sample.filename}>{sample.filename}</div>
      ) : (
        <div className="text-[10px] text-white/30 font-mono flex items-center gap-1">
          <UploadSimple size={11} weight="bold" /> drop or click
        </div>
      )}
      <input
        ref={inputRef}
        type="file"
        accept="audio/*,.wav,.mp3,.ogg,.flac,.m4a,.aac"
        className="hidden"
        onChange={(e) => upload(e.target.files?.[0])}
        data-testid={`kit-slot-${slot}-file-input`}
      />
      {sample && <audio ref={audioRef} src={`${BACKEND_URL}${sample.url}`} onEnded={() => setPlaying(false)} preload="metadata" />}
    </div>
  );
}

function KitCard({ kit, slots, onChange, onDelete, onSetDefault, busy, apiBase, gridCols = 4 }) {
  return (
    <div className="rounded-2xl p-4 space-y-3" style={{ background: "#0c0c0c", border: `1.5px solid ${kit.is_default ? "#34d399aa" : "#1c1c1c"}` }} data-testid={`kit-card-${kit.id}`}>
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <MusicNotes size={16} weight="fill" style={{ color: kit.is_default ? "#34d399" : "#a1a1aa" }} />
          <div className="font-display font-bold text-sm">{kit.name}</div>
          {kit.is_default && (
            <span className="px-2 py-0.5 rounded-full text-[9px] font-bold flex items-center gap-1" style={{ background: "#10b98122", color: "#34d399", border: "1px solid #10b98155" }}>
              <Star size={9} weight="fill" /> DEFAULT
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          {!kit.is_default && (
            <button
              onClick={() => onSetDefault(kit.id)}
              disabled={busy}
              className="px-3 py-1.5 rounded-md text-xs font-bold flex items-center gap-1 transition disabled:opacity-50"
              style={{ background: "linear-gradient(180deg,#10b981,#059669)", color: "#04140d" }}
              data-testid={`kit-card-${kit.id}-set-default`}
            >
              <CheckCircle size={12} weight="fill" /> Set as default
            </button>
          )}
          <button
            onClick={() => onDelete(kit.id)}
            disabled={busy}
            className="p-1.5 rounded-md hover:bg-red-500/20 text-red-400"
            title="Delete kit"
            data-testid={`kit-card-${kit.id}-delete`}
          >
            <Trash size={13} weight="bold" />
          </button>
        </div>
      </div>
      <div className="grid gap-2" style={{ gridTemplateColumns: `repeat(${gridCols}, minmax(0, 1fr))` }}>
        {slots.map((slot) => (
          <SlotDropZone key={slot} kit={kit} slot={slot} onChange={onChange} apiBase={apiBase} />
        ))}
      </div>
    </div>
  );
}

function KitsManager({
  apiBase, defaultSlots, title, testId, gridCols, manager, namePlaceholder, hint,
}) {
  const [kits, setKits] = useState([]);
  const [slots, setSlots] = useState(defaultSlots);
  const [newName, setNewName] = useState("");
  const [busy, setBusy] = useState(false);

  const reload = async () => {
    try {
      const { data } = await API.get(apiBase);
      setKits(data.kits || []);
      if (data.slots) setSlots(data.slots);
    } catch { /* noop */ }
  };
  useEffect(() => { reload(); }, [apiBase]);

  const createKit = async () => {
    const name = newName.trim() || `Kit ${kits.length + 1}`;
    setBusy(true);
    try {
      const { data } = await API.post(apiBase, { name });
      setKits((k) => [data.kit, ...k]);
      setNewName("");
      notifyKitsChanged({ source: apiBase, op: "create-kit", kitId: data.kit?.id });
      toast.success(`${title} created — drop samples into the slots`);
    } catch (e) { toast.error("Create failed: " + (e?.response?.data?.detail || e.message)); }
    finally { setBusy(false); }
  };

  const replaceKit = (updated) => setKits((k) => k.map((x) => x.id === updated.id ? updated : x));

  const deleteKit = async (kid) => {
    if (!window.confirm("Delete this kit and all its samples?")) return;
    setBusy(true);
    try {
      await API.delete(`${apiBase}/${kid}`);
      setKits((k) => k.filter((x) => x.id !== kid));
      manager?.refresh?.(true);
      notifyKitsChanged({ source: apiBase, op: "delete-kit", kitId: kid });
      toast.success("Kit deleted");
    } catch (e) { toast.error("Delete failed: " + (e?.response?.data?.detail || e.message)); }
    finally { setBusy(false); }
  };

  const setDefault = async (kid) => {
    setBusy(true);
    try {
      await API.post(`${apiBase}/${kid}/default`);
      setKits((k) => k.map((x) => ({ ...x, is_default: x.id === kid })));
      await manager?.refresh?.(true);
      notifyKitsChanged({ source: apiBase, op: "set-default", kitId: kid });
      toast.success("Default kit set · audio engines reloaded");
    } catch (e) { toast.error("Set-default failed: " + (e?.response?.data?.detail || e.message)); }
    finally { setBusy(false); }
  };

  return (
    <div className="rounded-2xl p-5 space-y-4" style={{ background: "#0c0c0c", border: "1px solid #1c1c1c" }} data-testid={testId}>
      <div className="flex items-center justify-between">
        <h2 className="font-display font-bold text-sm tracking-tight flex items-center gap-2">
          <MusicNotes size={14} weight="fill" /> {title}
          <span className="text-[10px] font-mono text-white/40 ml-1">{kits.length}</span>
        </h2>
      </div>
      <div className="flex items-center gap-2">
        <input
          type="text"
          value={newName}
          onChange={(e) => setNewName(e.target.value)}
          placeholder={namePlaceholder}
          className="flex-1 px-3 py-2 rounded-md bg-[#0a0a0a] border border-[#262626] text-sm"
          data-testid={`${testId}-new-name`}
          onKeyDown={(e) => { if (e.key === "Enter") createKit(); }}
        />
        <button
          onClick={createKit}
          disabled={busy}
          className="px-3 py-2 rounded-md text-sm font-bold flex items-center gap-1 disabled:opacity-50"
          style={{ background: "linear-gradient(180deg,#fff,#d4d4d4)", color: "#000" }}
          data-testid={`${testId}-new-create`}
        >
          <Plus size={12} weight="bold" /> New kit
        </button>
      </div>

      <div className="space-y-3 max-h-[34rem] overflow-y-auto pr-1">
        {kits.map((k) => (
          <KitCard
            key={k.id}
            kit={k}
            slots={slots}
            onChange={replaceKit}
            onDelete={deleteKit}
            onSetDefault={setDefault}
            busy={busy}
            apiBase={apiBase}
            gridCols={gridCols}
          />
        ))}
        {kits.length === 0 && (
          <div className="text-xs text-white/30 font-mono py-6 text-center">
            no kits yet — create one, then drop audio files into the slots.
          </div>
        )}
      </div>

      <div className="text-[10px] text-white/40 font-mono p-2 rounded-md" style={{ background: "#070707", border: "1px dashed #1f1f1f" }}>
        💡 {hint}
      </div>
    </div>
  );
}

export default function DrumKitsPanel() {
  return (
    <KitsManager
      apiBase="/training/kits"
      defaultSlots={["kick","snare","hihat","openhat","clap","perc","ride","tom"]}
      title="Drum kits"
      testId="drum-kits-panel"
      gridCols={4}
      manager={drumKitManager}
      namePlaceholder="Kit name (e.g. 808 Trap)"
      hint="Drop up to 8 audio samples (≤2MB each, WAV/MP3/OGG/FLAC). Set a kit as default and every drum hit in the Training Lab and the C.R.O.W. DAW will play your sample instead of the synthesized fallback."
    />
  );
}

export function BassKitsPanel() {
  return (
    <KitsManager
      apiBase="/training/bass-kits"
      defaultSlots={["C1","C2","C3","C4"]}
      title="Bass kits"
      testId="bass-kits-panel"
      gridCols={4}
      manager={bassKitManager}
      namePlaceholder="Bass kit name (e.g. Sub 808)"
      hint="Drop one bass sample per pitch (C1, C2, C3, C4). The default bass kit replaces the Galaxy Synth in the AI Training Lab and the DAW. Notes between uploaded pitches are interpolated automatically."
    />
  );
}

export function MelodyKitsPanel() {
  return (
    <KitsManager
      apiBase="/training/melody-kits"
      defaultSlots={["flute","piano","lead","brass","strings"]}
      title="Melody kits"
      testId="melody-kits-panel"
      gridCols={5}
      manager={melodyKitManager}
      namePlaceholder="Melody kit name (e.g. Cinematic Pads)"
      hint="Drop one single-note sample per instrument family (flute, piano, lead, brass, strings — recommended root note C4). The default melody kit overrides the synthesized lead in the AI Beat Lab and the DAW; the highest-priority loaded slot (piano → lead → brass → strings → flute) is used."
    />
  );
}
